import { useState, useEffect } from "react";

//MUI IMPORTS
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Grid,
  Button,
  Box,
  Paper,
  Chip,
} from "@mui/material";
import ReceiptIcon from "@mui/icons-material/Receipt";
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import Alert from "@mui/material/Alert";
import { useNavigate } from "react-router-dom";

//CUSTOM IMPORTS
import { verEnviarPDF } from "../../../services/services";
import { UrlApi } from "../../../services/apirest";
import { getData } from "../../../services/library";
import CustomBackdrop from "../../CustomBackdrop";
import { formatoPesoArgentino } from "../../../services/library";
import DialogFinalizacion from "./DialogoFinalizacion";

export default function PreFactura({
  openPrefactura,
  regPrefactura,
  closeNewInvoiceDialog,
  closePrefactura,
  tipoComp,
}) {
  /* ----------------------------ESTADOS Y REFS-------------------------------  */
  const preFactura = JSON.parse(regPrefactura?.data);
  const { cliente, empresa, feCAEReq, items } = preFactura;
  const { factura } = feCAEReq; //datos de la prefactura
  const [dataTipoCbte, setDataTipoCbte] = useState(); //tipos de comprobanets
  const [porcIva, setPorcIva] = useState(); //porcentajes de IVA || VAT -> denominacion internacional para IVA (value added tax o impuesto al valor agregado!!)
  const [facturaId, setFacturaId] = useState(0); //id de la factura emitida que guardo para enviar a la funcion de visualización de PDF.
  const [cargaRecibo, setCargaRecibo] = useState(
    cliente?.id_condicionpago == 1 && tipoComp == 1,
  );
  const navigate = useNavigate();

  //Estados que manejan visuales
  const [showDialog, setShowDialog] = useState(false);
  const [disBtn, setDisBtn] = useState(false);
  const [superaMonto, setSuperaMonto] = useState(false);
  const [loading, setLoading] = useState(false);

  /* --------------------------- HANDLERS --------------------------------  */
  const handleClickConfirm = async (preFacturaId) => {
    try {
      const solic = await fetch(
        `${UrlApi}/prefacturas/facturar/${preFacturaId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      const res = await solic.json();
      if (res.aprobada) {
        setShowDialog(true);
        setFacturaId(res.id_factura);
      } else {
        const prefact = await fetch(`${UrlApi}/prefacturas/${preFacturaId}`);
        const { aprobada_arca } = await prefact.json();
        if (aprobada_arca)
          alert(
            "El comprobante se aprobó por ARCA, pero no pudieron guardarse sus datos. Contacte a soporte.",
          );
        else
          alert(
            "Error: " + res?.detalles || "No se pudo emitir el comprobante.",
          );
        setShowDialog(false);
        closePrefactura();
        closeNewInvoiceDialog();
      }
    } catch (error) {
      console.log("Error.");
      alert("Error Desconocido. Contacte a soporte.");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseAll = () => {
    setShowDialog(false);
    try {
      closePrefactura();
      !!closeNewInvoiceDialog
        ? closeNewInvoiceDialog()
        : window.location.reload();
    } catch (error) {
      window.location.reload();
    }
  };

  /* --------------------------- EFFECTS --------------------------------  */
  useEffect(() => {
    getData(
      `/facturacion_tipos_comprobantes/${factura.CbteTipo}`,
      setDataTipoCbte,
    );
    getData("/porcentaje_iva", setPorcIva);
  }, []);

  useEffect(() => {
    if (
      dataTipoCbte?.monto_maximo &&
      dataTipoCbte?.monto_maximo < factura.ImpNeto
    )
      setSuperaMonto(true);
    else setSuperaMonto(false);
  }, [dataTipoCbte]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (loading) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [loading]);

  /* --------------------------- STYLING --------------------------------  */

  const fontSize = "12px";
  const cellPadding = "4px 8px";

  const colores = {
    colorCliente: "#709775",
    colorTitulo: "primary.main",
    warning: "#E6A700",
  };

  // Estilos compactos para las celdas de tabla
  const compactCellSx = {
    padding: cellPadding,
    fontSize: fontSize,
    lineHeight: 1.2,
  };

  const compactHeaderCellSx = {
    ...compactCellSx,
    fontWeight: 600,
    backgroundColor: "#f1f5f9",
  };

  /* --------------------------- RENDER --------------------------------  */
  return (
    <>
      <Dialog 
        open={openPrefactura} 
        onClose={(event, reason) => {
          if (loading) {
            alert("No puede realizarse esta acción. Hay una operación en curso.")
            return;
          }
          closePrefactura();
        }}
        fullScreen
        >
        <DialogTitle
          sx={{
            backgroundColor: colores.colorTitulo,
            color: "#fff",
            py: 0.75,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <ReceiptIcon sx={{ fontSize: 20 }} />
          <Typography variant="subtitle2" component="span" fontWeight="500">
            Vista previa de comprobante a emitir
          </Typography>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{ backgroundColor: "#f8f9fa", p: 2, overflow: "auto" }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              height: "100%",
            }}
          >
            {superaMonto && (
              <Alert severity="warning" sx={{ py: 0, flexShrink: 0 }}>
                {dataTipoCbte?.leyenda_supera_monto}
              </Alert>
            )}

            {/* FILA SUPERIOR: Cliente | Emisor */}
            <Box sx={{ display: "flex", gap: 1.5, flexShrink: 0 }}>
              {/* Card Cliente */}
              <Paper
                elevation={1}
                sx={{
                  p: 1.5,
                  borderLeft: `3px solid ${colores.colorCliente}`,
                  flex: 1,
                }}
              >
                <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                  <PersonIcon
                    sx={{ color: colores.colorCliente, fontSize: 18 }}
                  />
                  <Typography
                    variant="subtitle2"
                    fontWeight="600"
                    sx={{ color: colores.colorCliente }}
                  >
                    Cliente
                  </Typography>
                </Box>
                <Box
                  display="flex"
                  alignItems="center"
                  gap={0.5}
                  flexWrap="wrap"
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      color: "#495057",
                      fontSize: fontSize,
                    }}
                  >
                    {cliente?.razon_social}
                  </Typography>
                  <Chip
                    label={`${cliente.cuit.length > 1 ? cliente.cuit : "No informa."}`}
                    size="small"
                    sx={{
                      height: 18,
                      background:
                        cliente.cuit.length > 1
                          ? colores.colorCliente
                          : colores.warning,
                      color: "white",
                      fontSize: "10px",
                      "& .MuiChip-label": { px: 0.75 },
                      borderColor:
                        cliente.cuit.length > 1
                          ? colores.colorCliente
                          : "#CC9400",
                    }}
                  />
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ fontSize: "11px" }}
                >
                  <strong>Domicilio:</strong>{" "}
                  {cliente?.direccion_fiscal || "No informa"}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ fontSize: "11px" }}
                >
                  <strong>Condición IVA:</strong> {cliente?.descr_condiva} |{" "}
                  <strong>Concidión VENTA:</strong> {cliente?.descr_condpago}
                </Typography>
              </Paper>
              <Paper
                elevation={1}
                sx={{ p: 1.5, borderLeft: `3px solid #475569`, flex: 1 }}
              >
                <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                  <BusinessIcon sx={{ color: "#475569", fontSize: 18 }} />
                  <Typography
                    variant="subtitle2"
                    fontWeight="600"
                    sx={{ color: "#475569" }}
                  >
                    Emisor
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={0.5}
                      flexWrap="wrap"
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: "#495057",
                          fontSize: fontSize,
                        }}
                      >
                        {empresa?.empresa?.toUpperCase()}
                      </Typography>
                      <Chip
                        label={empresa?.cuit}
                        size="small"
                        sx={{
                          height: 18,
                          backgroundColor: "#475569",
                          color: "white",
                          fontSize: "10px",
                          "& .MuiChip-label": { px: 0.75 },
                        }}
                      />
                    </Box>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ fontSize: "11px" }}
                    >
                      <strong>Punto de Venta: </strong> {factura?.PtoVta}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ fontSize: "11px" }}
                    >
                      <strong>Tipo Comprobante:</strong>{" "}
                      {dataTipoCbte?.descripcion || "..."}
                    </Typography>
                  </Box>
                  {empresa?.logo && (
                    <Box sx={{ width: 100, height: 60, flexShrink: 0 }}>
                      <img
                        src={empresa?.logo}
                        alt="Logo"
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          objectFit: "contain",
                        }}
                      />
                    </Box>
                  )}
                </Box>
              </Paper>
            </Box>
            {/* TABLAS: Items e Impuestos en fila */}
            <Box sx={{ display: "flex", gap: 1.5, flex: 1, minHeight: 0 }}>
              {/* Tabla de Items */}
              <Paper
                elevation={1}
                sx={{
                  flex: 1.4,
                  overflow: "auto",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={compactHeaderCellSx}>
                        Descripción
                      </TableCell>
                      <TableCell align="right" sx={compactHeaderCellSx}>
                        Cantidad
                      </TableCell>
                      <TableCell align="right" sx={compactHeaderCellSx}>
                        Precio Unitario
                      </TableCell>
                      <TableCell align="right" sx={compactHeaderCellSx}>
                        Subtotal
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items?.map((item, i) => (
                      <TableRow
                        key={i}
                        sx={{
                          "&:nth-of-type(odd)": { backgroundColor: "#fafafa" },
                        }}
                      >
                        <TableCell sx={compactCellSx}>
                          {item?.detalle}
                        </TableCell>
                        <TableCell align="right" sx={compactCellSx}>
                          {item?.cantidad?.toString().replace(".", ",") || 0}
                        </TableCell>
                        <TableCell align="right" sx={compactCellSx}>
                          {formatoPesoArgentino(item.prun_siniva)}
                        </TableCell>
                        <TableCell align="right" sx={compactCellSx}>
                          {formatoPesoArgentino(item.total_renglon_siniva)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ backgroundColor: "#e2e8f0" }}>
                      <TableCell
                        colSpan={3}
                        align="right"
                        sx={{ ...compactCellSx, fontWeight: 600 }}
                      >
                        Importe Neto
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ ...compactCellSx, fontWeight: 600 }}
                      >
                        {formatoPesoArgentino(factura?.ImpNeto)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Paper>

              {/* Tabla de Impuestos */}
              <Paper
                elevation={1}
                sx={{
                  flex: 1,
                  overflow: "auto",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={compactHeaderCellSx}>Impuesto</TableCell>
                      <TableCell align="center" sx={compactHeaderCellSx}>
                        Alic.
                      </TableCell>
                      <TableCell align="right" sx={compactHeaderCellSx}>
                        Base Imp.
                      </TableCell>
                      <TableCell align="center" sx={compactHeaderCellSx}>
                        Exenc.
                      </TableCell>
                      <TableCell align="right" sx={compactHeaderCellSx}>
                        Importe
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {factura?.Iva?.AlicIva?.map((iva, i) => (
                      <TableRow
                        key={`iva-${i}`}
                        sx={{
                          "&:nth-of-type(odd)": { backgroundColor: "#fafafa" },
                        }}
                      >
                        <TableCell sx={compactCellSx}>
                          <i>IVA</i>
                        </TableCell>
                        <TableCell align="center" sx={compactCellSx}>
                          <Chip
                            label={`${porcIva?.find((porc) => porc.codigo_afip === iva.Id)?.descripcion}%`}
                            size="small"
                            sx={{
                              height: 18,
                              backgroundColor: "#0891b2",
                              color: "white",
                              fontSize: "10px",
                              "& .MuiChip-label": { px: 0.5 },
                            }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={compactCellSx}>
                          {formatoPesoArgentino(iva.BaseImp)}
                        </TableCell>
                        <TableCell align="center" sx={compactCellSx}>
                          -
                        </TableCell>
                        <TableCell align="right" sx={compactCellSx}>
                          {formatoPesoArgentino(iva.Importe)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {factura?.Tributos?.Tributo?.map((tributo, i) => (
                      <TableRow
                        key={`trib-${i}`}
                        sx={{
                          "&:nth-of-type(odd)": { backgroundColor: "#fafafa" },
                        }}
                      >
                        <TableCell sx={compactCellSx}>{tributo.Desc}</TableCell>
                        <TableCell align="center" sx={compactCellSx}>
                          <Chip
                            label={`${tributo.alicuota_regulada}%`}
                            size="small"
                            sx={{
                              height: 18,
                              backgroundColor: "#0891b2",
                              color: "white",
                              fontSize: "10px",
                              "& .MuiChip-label": { px: 0.5 },
                            }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={compactCellSx}>
                          {formatoPesoArgentino(tributo.BaseImp)}
                        </TableCell>
                        <TableCell align="center" sx={compactCellSx}>
                          {tributo.exencion ? (
                            <Chip
                              label={`${tributo.exencion}%`}
                              size="small"
                              sx={{
                                height: 18,
                                backgroundColor: "#F4320B",
                                color: "white",
                                fontSize: "10px",
                                "& .MuiChip-label": { px: 0.5 },
                              }}
                            />
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell align="right" sx={compactCellSx}>
                          {formatoPesoArgentino(tributo.Importe)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </Box>

            {/* FILA INFERIOR: Total */}
            <Paper
              elevation={2}
              sx={{
                backgroundColor: "secondary.main",
                color: "white",
                p: 1.5,
                borderRadius: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexShrink: 0,
              }}
            >
              <Typography variant="subtitle1" fontWeight="600">
                IMPORTE TOTAL
              </Typography>
              <Typography variant="h5" fontWeight="700">
                {formatoPesoArgentino(factura?.ImpTotal)}
              </Typography>
            </Paper>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 1.5, backgroundColor: "#f8fafc" }}>
          <Button
            onClick={closePrefactura}
            variant="outlined"
            color="error"
            size="small"
          >
            Cancelar
          </Button>
          <Button
            disabled={disBtn || superaMonto}
            onClick={() => {
              setLoading(true);
              setDisBtn(true);
              handleClickConfirm(regPrefactura.id_prefactura);
            }}
            variant="contained"
            size="small"
            sx={{
              backgroundColor: "#0891b2",
              "&:hover": {
                backgroundColor: "#0e7490",
              },
            }}
          >
            Confirmar y emitir
          </Button>
        </DialogActions>

        <CustomBackdrop open={loading} text="Emitiendo factura..." />
      </Dialog>
      {showDialog && (
        <DialogFinalizacion
          maxWidth={"lg"}
          open={showDialog}
          cliente={cliente}
          factura={factura}
          facturaId={facturaId}
          disIrCtaCte={Boolean(preFactura.pdf.envasado)}
          handleCloseAll={handleCloseAll}
          cargaRecibo={cargaRecibo}
          setCargaRecibo={setCargaRecibo}
          title="Comprobante emitido! "
          subtitle="Se ha registrado correctamente"
          items={["Item 1", "Item 2"]}
          onVerComprobante={() => verEnviarPDF(facturaId, 0)}
          onCuentaCorriente={() => {
            navigate(`/clientes/profile/${cliente.id}/cc`);
          }}
          onIrAlInicio={() => {
            navigate("/navbar");
          }}
          onLimpiarPantalla={() => {
            try {
              closePrefactura();
              !!closeNewInvoiceDialog
                ? closeNewInvoiceDialog()
                : window.location.reload();
            } catch (error) {
              window.location.reload();
            }
          }}
        />
      )}
    </>
  );
}
